<template>
    <div>
        <DataTable :value="documents" lazy paginator dataKey="id"
                   v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset"
                   removableSort :totalRecords="totalRecords" :loading="loading" @page="onPage"
                   @sort="onPage" :rowsPerPageOptions="[5,10,20,50]"
                   v-model:filters="searchCriteria"
                   filterDisplay="row" class="documents-table" scrollable
                   scrollHeight="calc(100vh - 300px)">
            <Column field="creationDate" :header="$t('message.documentList.table.date')"
                    :sortable="true" filterField="creationDate"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #filter>
                    <CustomCalendar name="creationDate" v-model="searchCriteria.creationDate.value" selectionMode="range"
                                    :showTime="false" :placeholder="$t('message.horseList.yearOrRange')" dateFormat="yy" view="year" showButtonBar
                                    @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="documentType" :header="$t('message.documentList.table.documentType')"
                    :sortable="true" filterField="documentType"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.documentType }}
                </template>
                <template #filter>
                    <CustomMultiSelectDocumentType name="documentType" v-model="searchCriteria.documentTypeList.value"
                                                   @update:modelValue="search" />
                </template>
            </Column>
            <Column field="horse.studbookRegistryId" :header="$t('message.documentList.table.studbookRegistryId')"
                    :sortable="true" filterField="studbookRegistryId"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.horseStudbookRegistryId }}
                </template>
                <template #filter>
                    <CustomInputText name="studbookRegistryId" v-model="searchCriteria.studbookRegistryId.value"
                                     @update:modelValue="search" />
                </template>
            </Column>
            <Column field="horse.name" :header="$t('message.documentList.table.horseName')"
                    :sortable="true" filterField="name"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #body="{ data }">
                    {{ data.horseName }}
                </template>
                <template #filter>
                    <CustomInputText name="name" v-model="searchCriteria.name.value"
                                     class="mb-0" @update:modelValue="search" />
                </template>
            </Column>
            <Column :header="$t('message.documentList.table.pdf')" class="w-1 text-center">
                <template #body="{ data }">
                    <CustomIconButton icon="pi pi-download" onlyIcon @click="downloadPdf(data)"/>
                </template>
            </Column>
            <template #empty>
                {{ $t('message.other.emptyTable') }}
            </template>
        </DataTable>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {FilterMatchMode} from "primevue/api";
import {
    searchDocument,
    searchDocumentCount,
} from "@/swagger/vue-api-client";
import CustomInputText from "@/components/form/CustomInputText.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import {SearchCriteria} from "@/utils/SearchCriteria";
import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomSelectOneHorseOrigin from "@/components/form/CustomSelectOneHorseOrigin.vue";
import CustomIconButton from "@/components/form/CustomIconButton.vue";
import {FileUtils} from "@/utils/FileUtils";
import CustomMultiSelectDocumentType from "@/components/form/CustomMultiSelectDocumentType.vue";

export default {
    name: "DocumentTable",
    components: {
      CustomMultiSelectDocumentType,
        CustomIconButton,
        CustomSelectOneHorseOrigin,
        CustomCalendar,
        Column,
        DataTable,
        CustomInputNumber,
        CustomInputText,
        CustomMultiSelectHorseColor,
    },

    props: {
        modelValue: null,
    },

    data() {
        return {
            loading: false,
            totalRecords: 0,
            documents: [],
            searchCriteria: this.getClearSearchCriteria(),
        };
    },

    mounted() {
        this.searchCriteria = this.getClearSearchCriteria();
        this.search();
    },

    methods: {
        search() {
            this.onPage(this.getFirstPage());
        },

        onPage(event) {
            this.loading = true;

            this.searchCriteria.page.offset = event.first;
            this.searchCriteria.page.limit = event.rows;
            this.searchCriteria.page.sortField = event.sortField ? event.sortField : "id";
            this.searchCriteria.page.sortOrder = event.sortOrder ? event.sortOrder : -1;

            const body = SearchCriteria.prepareCriteria(this.searchCriteria, true);

            searchDocument({body}).then((response) => {
                this.documents = response.data;
                return searchDocumentCount({body})
                    .then((response) => {
                        this.totalRecords = response.data;
                        this.loading = false;
                    });
            });
        },

        getFirstPage() {
            return {
                first: this.searchCriteria.page.offset,
                rows: this.searchCriteria.page.limit,
            };
        },

        getClearSearchCriteria() {
            return {
                creationDate: { value: null, matchMode: FilterMatchMode.IN },
                documentTypeList: { value: null, matchMode: FilterMatchMode.IN },
                studbookRegistryId: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: { value: null, matchMode: FilterMatchMode.CONTAINS },
                page: {
                    limit: 10,
                    offset: 0,
                    sortField: null,
                    sortOrder: null,
                },
            };
        },

        downloadPdf(file) {
            const fileName = FileUtils.toSafeFileName(file.horseName + "_" + file.documentType, "pdf", file.documentType);
            const url = `/files/${encodeURIComponent(file.id)}`;

            FileUtils.downloadFileUsingGET(fileName, url);
        },
    },
};
</script>

<style lang="scss">
    .preserve-newlines {
        white-space: pre-line;
    }
</style>
